import { useAuth } from "context/AuthContext";
import React, { useEffect, useRef, useState } from "react";
import NotificationAlert from "react-notification-alert";
import { BANK_MANAGEMENT_USERS } from "services/consts";
import axios from "axios";
import {
    CardBody,
    Spinner,
    Table,
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    Row,
    Col,
} from "reactstrap";

export const BankManagement = () => {
    const { user, tokenGd } = useAuth();
    const notificationAlert = useRef(null);
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [modalMode, setModalMode] = useState("create"); // 'create' | 'update'
    const [selectedBank, setSelectedBank] = useState(null);
    const [bankName, setBankName] = useState("");
    const [bankId, setBankId] = useState("");
    const [imageId, setImageId] = useState(null);
    const [imageUrl, setImageUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const notify = (place, color, message) => {
        var options = {};
        options = {
            place: place,
            message: (
                <div>
                    <div>{message}</div>
                </div>
            ),
            type: color,
            icon: "now-ui-icons ui-1_bell-53",
            autoDismiss: 7,
        };
        notificationAlert.current.notificationAlert(options);
    };

    const toggleModal = () => {
        setModal(!modal);
        if (modal) {
            // Reset form state when closing modal
            setBankName("");
            setBankId("");
            setImageId(null);
            setImageUrl(null);
            setModalMode("create");
            setSelectedBank(null);
        }
    };

    const handleBankNameChange = e => {
        const value = e.target.value;
        if (value.length <= 255) {
            setBankName(value);
        }
    };

    const handleBankIdChange = e => {
        const value = e.target.value;
        // Filter non-numeric characters and enforce max 3 digits
        const numericValue = value.replace(/[^0-9]/g, "").slice(0, 3);
        setBankId(numericValue);
    };

    const handleImageUpload = async e => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const validTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp",
        ];
        if (!validTypes.includes(file.type)) {
            notify(
                "tr",
                "danger",
                "Tipo de arquivo inválido. Use JPG, JPEG, PNG ou WebP."
            );
            return;
        }

        // Validate file size (3MB = 3 * 1024 * 1024 bytes)
        const maxSize = 3 * 1024 * 1024;
        if (file.size > maxSize) {
            notify("tr", "danger", "Arquivo excede o tamanho máximo de 3MB.");
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append("file", file);

            const res = await axios.post(
                "https://backend.takeat.app/restaurants/files",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${tokenGd}`,
                    },
                }
            );

            setImageId(res.data.id);
            setImageUrl(res.data.url);
        } catch (err) {
            notify(
                "tr",
                "danger",
                err?.response?.data?.message ||
                    "Erro ao fazer upload da imagem."
            );
        } finally {
            setUploading(false);
        }
    };

    const getBanks = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                "https://backend.takeat.app/public/banks"
            );
            setBanks(res.data);
        } catch (err) {
            notify("tr", "danger", "Erro ao carregar bancos.");
        } finally {
            setLoading(false);
        }
    };

    const handlePlaceholderClick = bank => {
        setSelectedBank(bank);
        setModalMode("update");
        setModal(true);
    };

    const handleImageClick = bank => {
        setSelectedBank(bank);
        setModalMode("update");
        setModal(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);

            if (modalMode === "create") {
                // Create new bank
                const res = await axios.post(
                    "https://backend.takeat.app/admin/banks",
                    {
                        bank_id: bankId,
                        name: bankName,
                        image_id: imageId,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${tokenGd}`,
                        },
                    }
                );

                // Append the new bank to the list
                setBanks([...banks, res.data]);
                notify("tr", "success", "Banco criado com sucesso!");
            } else if (modalMode === "update") {
                // Update existing bank image
                const res = await axios.post(
                    "https://backend.takeat.app/admin/banks/update-image",
                    {
                        bank_id: selectedBank.bank_id,
                        image_id: imageId,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${tokenGd}`,
                        },
                    }
                );

                // Update the bank in the list
                setBanks(
                    banks.map(bank =>
                        bank.bank_id === selectedBank.bank_id ? res.data : bank
                    )
                );
                notify(
                    "tr",
                    "success",
                    "Imagem do banco atualizada com sucesso!"
                );
            }

            // Close modal and reset form
            toggleModal();
        } catch (err) {
            const errorMessage =
                modalMode === "create"
                    ? err?.response?.data?.message || "Erro ao criar banco."
                    : err?.response?.data?.message ||
                      "Erro ao atualizar imagem do banco.";
            notify("tr", "danger", errorMessage);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (!BANK_MANAGEMENT_USERS.includes(user.user_id)) {
            window.location.href = "/settings";
        } else {
            getBanks();
        }
    }, [user]);

    return (
        <>
            <NotificationAlert ref={notificationAlert} />
            <div className="content">
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                    }}
                >
                    <h3>Gerenciar Bancos</h3>
                    <Button
                        className="btn-round"
                        color="success"
                        outline
                        onClick={() => toggleModal()}
                    >
                        <i className="fa fa-plus" />
                        Criar Banco
                    </Button>
                </div>

                <CardBody>
                    {loading ? (
                        <div
                            style={{
                                color: "red",
                                padding: 15,
                                textAlign: "center",
                            }}
                        >
                            <Spinner />
                        </div>
                    ) : (
                        <Table>
                            <thead>
                                <tr>
                                    <th>Imagem</th>
                                    <th>ID do Banco</th>
                                    <th>Nome</th>
                                </tr>
                            </thead>
                            <tbody>
                                {banks.map(bank => (
                                    <tr key={bank.bank_id}>
                                        <td>
                                            {bank.image?.url ? (
                                                <img
                                                    src={bank.image.url}
                                                    alt={bank.name}
                                                    onClick={() =>
                                                        handleImageClick(bank)
                                                    }
                                                    style={{
                                                        height: 40,
                                                        width: "auto",
                                                        cursor: "pointer",
                                                        transition:
                                                            "all 0.2s ease",
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.opacity =
                                                            "0.7";
                                                        e.currentTarget.style.transform =
                                                            "scale(1.05)";
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.opacity =
                                                            "1";
                                                        e.currentTarget.style.transform =
                                                            "scale(1)";
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    onClick={() =>
                                                        handlePlaceholderClick(
                                                            bank
                                                        )
                                                    }
                                                    style={{
                                                        height: 40,
                                                        width: 60,
                                                        border: "2px dashed #ccc",
                                                        borderRadius: 4,
                                                        backgroundColor:
                                                            "#f5f5f5",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "center",
                                                        cursor: "pointer",
                                                        fontSize: 12,
                                                        color: "#999",
                                                        transition:
                                                            "all 0.2s ease",
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.backgroundColor =
                                                            "#efefef";
                                                        e.currentTarget.style.borderColor =
                                                            "#999";
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.backgroundColor =
                                                            "#f5f5f5";
                                                        e.currentTarget.style.borderColor =
                                                            "#ccc";
                                                    }}
                                                >
                                                    +
                                                </div>
                                            )}
                                        </td>
                                        <td>{bank.bank_id}</td>
                                        <td>{bank.name}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>

                <Modal isOpen={modal} toggle={toggleModal} size="md">
                    <ModalBody>
                        {modalMode === "create" && (
                            <>
                                <Row>
                                    <Col>
                                        <label>Nome do Banco</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={bankName}
                                            onChange={handleBankNameChange}
                                            placeholder="Digite o nome do banco"
                                            maxLength="255"
                                        />
                                        <small style={{ color: "#999" }}>
                                            {bankName.length}/255 caracteres
                                        </small>
                                    </Col>
                                </Row>
                                <Row style={{ marginTop: 15 }}>
                                    <Col>
                                        <label>ID do Banco</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={bankId}
                                            onChange={handleBankIdChange}
                                            placeholder="Digite o ID (máx 3 dígitos)"
                                            maxLength="3"
                                        />
                                        <small style={{ color: "#999" }}>
                                            Apenas números (0-9)
                                        </small>
                                    </Col>
                                </Row>
                            </>
                        )}
                        {modalMode === "update" && selectedBank?.image?.url && (
                            <Row style={{ marginBottom: 15 }}>
                                <Col>
                                    <label>Imagem Atual</label>
                                    <div
                                        style={{
                                            border: "1px solid #ddd",
                                            borderRadius: 8,
                                            padding: 10,
                                            textAlign: "center",
                                            backgroundColor: "#f9f9f9",
                                        }}
                                    >
                                        <img
                                            src={selectedBank.image.url}
                                            alt={selectedBank.name}
                                            style={{
                                                maxHeight: 100,
                                                maxWidth: "100%",
                                            }}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        )}
                        <Row
                            style={{
                                marginTop: modalMode === "create" ? 15 : 0,
                            }}
                        >
                            <Col>
                                <label>Logo do Banco</label>
                                <div
                                    style={{
                                        border: "2px dashed #ccc",
                                        borderRadius: 8,
                                        padding: 20,
                                        textAlign: "center",
                                        position: "relative",
                                        minHeight: 120,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: imageUrl
                                            ? "#f9f9f9"
                                            : "#fafafa",
                                    }}
                                >
                                    {uploading ? (
                                        <div style={{ textAlign: "center" }}>
                                            <Spinner size="sm" />
                                            <p
                                                style={{
                                                    marginTop: 10,
                                                    color: "#999",
                                                }}
                                            >
                                                Enviando...
                                            </p>
                                        </div>
                                    ) : imageUrl ? (
                                        <img
                                            src={imageUrl}
                                            alt="Preview"
                                            style={{
                                                maxHeight: 100,
                                                maxWidth: "100%",
                                            }}
                                        />
                                    ) : (
                                        <div>
                                            <p
                                                style={{
                                                    color: "#999",
                                                    marginBottom: 10,
                                                }}
                                            >
                                                Clique para selecionar uma
                                                imagem
                                            </p>
                                            <small style={{ color: "#bbb" }}>
                                                JPG, JPEG, WebP ou PNG (máx 3MB)
                                            </small>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/jpg,image/jpeg,image/webp,image/png"
                                        onChange={handleImageUpload}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            height: "100%",
                                            opacity: 0,
                                            cursor: "pointer",
                                        }}
                                        disabled={uploading}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggleModal}>
                            Cancelar
                        </Button>
                        <Button
                            color="success"
                            disabled={
                                saving ||
                                (modalMode === "create" &&
                                    (!bankId || !bankName || !imageId)) ||
                                (modalMode === "update" && !imageId)
                            }
                            onClick={handleSave}
                        >
                            {saving ? <Spinner size="sm" /> : "Salvar"}
                        </Button>
                    </ModalFooter>
                </Modal>
            </div>
        </>
    );
};
